import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { api, API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { fmtDate, fmtNum } from "@/lib/format";
import { payKey } from "@/lib/tpv";
import { elementToPdf } from "@/lib/pdf";
import { FACES } from "@/components/reports/Rating";

function Rate({ id, initial, t }) {
  const [val, setVal] = useState(initial || 0);
  const [busy, setBusy] = useState(false);
  const send = async (score) => {
    if (val || busy) return;
    setBusy(true);
    try { const { data } = await api.post(`/public/receipt/${id}/rating`, { score }); setVal(data.rating); } catch { /* ignore */ } finally { setBusy(false); }
  };
  return (
    <div className="bg-white w-full max-w-sm shadow-md rounded-xl p-4 text-center" data-testid="receipt-rating" data-value={val}>
      <div className="font-bold text-sm">{val ? t("rating_thanks") : t("rating_ask")}</div>
      <div className="flex justify-center gap-6 mt-3">
        {FACES.map(([v, Icon, c]) => (
          <button key={v} type="button" onClick={() => send(v)} disabled={!!val || busy} className={`w-16 h-16 rounded-full grid place-items-center border-2 transition-transform ${val === v ? "scale-110 border-current" : "border-transparent"} ${val && val !== v ? "opacity-30" : "active:scale-95"}`} style={{ color: c, background: `${c}18` }} data-testid={`rating-btn-${v}`}><Icon className="w-9 h-9" /></button>
        ))}
      </div>
    </div>
  );
}

export default function PublicReceipt() {
  const { t, lang, setLang } = useApp();
  const params = new URLSearchParams(window.location.search);
  const id = params.get("id");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/public/receipt/${id}`).then((r) => setData(r.data)).catch(() => setErr(true)); }, [id]);
  useEffect(() => {
    const want = params.get("lang") || data?.sale?.lang;
    if (want && ["es", "ar", "fr"].includes(want) && want !== lang) setLang(want);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <div className="min-h-screen grid place-items-center text-center p-6" data-testid="public-receipt-error">{t("receipt_not_found")}</div>;
  if (!data) return <div className="min-h-screen grid place-items-center text-muted-foreground">…</div>;
  const { sale, shop } = data;
  return (
    <div className="min-h-screen bg-[#F0F0F0] flex flex-col items-center p-4 gap-4" data-testid="public-receipt">
      <div className="flex gap-1 self-end" data-testid="receipt-lang-switch">{[["ar", "عربي"], ["fr", "FR"], ["es", "ES"]].map(([c, l]) => <button key={c} type="button" onClick={() => setLang(c)} className={`h-8 px-3 rounded-full text-xs font-bold border ${lang === c ? "bg-[#1F4E8C] text-white border-[#1F4E8C]" : "bg-white border-border"}`} data-testid={`receipt-lang-${c}`}>{l}</button>)}</div>
      <Rate id={id} initial={sale.rating} t={t} />
      <div id="pub-receipt" className="font-mono-receipt text-sm p-5 bg-white w-full max-w-sm shadow-md">
        <div className="text-center border-b border-dashed border-foreground/40 pb-3 mb-3">
          {shop.logo_id && <img src={`${API}/public/receipt/${id}/logo`} alt="" className="w-16 h-16 object-contain mx-auto mb-1" />}
          <div className="text-lg font-bold" data-testid="public-receipt-shop">{shop.name}</div>
          {shop.address && <div>{shop.address}</div>}
          {shop.phone && <div dir="ltr">{shop.phone}</div>}
          <div>{fmtDate(sale.created_at, lang)}</div>
          <div dir="ltr" data-testid="public-receipt-no">#{sale.receipt_no} · {sale.staff_name}</div>
        </div>
        {sale.items.map((i, n) => (
          <div key={n} className="flex justify-between gap-2"><span className="break-words min-w-0" dir="auto">{i.qty}{i.ppk ? " kg" : ""} × {i.name}</span><span dir="ltr" className="shrink-0">{fmtNum(i.price * i.qty)}</span></div>
        ))}
        <div className="border-t border-dashed border-foreground/40 mt-3 pt-3 space-y-0.5">
          {sale.discount > 0 && <div className="flex justify-between"><span>{t("discount")}</span><span dir="ltr">-{fmtNum(sale.discount)}</span></div>}
          <div className="flex justify-between font-bold text-base"><span>{t("total")}</span><span dir="ltr" data-testid="public-receipt-total">{fmtNum(sale.total)}</span></div>
          {sale.charity > 0 && <div className="flex justify-between" data-testid="public-receipt-charity"><span>{t("charity")}</span><span dir="ltr">{fmtNum(sale.charity)}</span></div>}
          {sale.charity > 0 && <div className="flex justify-between font-bold"><span>{t("total_paid")}</span><span dir="ltr">{fmtNum(sale.total + sale.charity)}</span></div>}
          <div className="flex justify-between"><span>{t("payment")}</span><span>{t(payKey(sale.payment))}</span></div>
          {sale.given > sale.total + (sale.charity || 0) && <div className="flex justify-between"><span>{t("received")}</span><span dir="ltr">{fmtNum(sale.given)}</span></div>}
          {sale.given > sale.total + (sale.charity || 0) && <div className="flex justify-between"><span>{t("change")}</span><span dir="ltr">{fmtNum(sale.given - sale.total - (sale.charity || 0))}</span></div>}
        </div>
        {shop.doc_footer && <div className="text-xs text-center mt-3 whitespace-pre-wrap">{shop.doc_footer}</div>}
        <div className="text-center mt-4">{t("thanks")}</div>
        <div className="mt-3 pt-2 border-t border-dashed border-foreground/30 flex items-center justify-center gap-1.5 text-[11px] text-foreground/60"><img src="/brand/orbit-logo-mono-96.png" alt="" className="w-5 h-5" />{t("powered_by")} Orbit PMS · مدار</div>
      </div>
      <button type="button" onClick={async () => { window.scrollTo(0, 0); await new Promise((r) => setTimeout(r, 150)); elementToPdf(document.getElementById("pub-receipt"), `receipt-${sale.receipt_no}.pdf`, { receipt: true }); }} className="h-12 px-6 rounded-xl bg-[#1F4E8C] text-white font-bold flex items-center gap-2" data-testid="public-receipt-pdf"><Download className="w-5 h-5" />PDF</button>
    </div>
  );
}
